import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Adresse, TypeLieu } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import type { Pagination } from './types.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';

/**
 * Lieux — `/v1/g/:slug/lieux` (`docs/dev/api.md` § 13, 12.1). Lecture ouverte
 * à `artworks:read`, écriture réservée à `settings:locations` : la régie ne
 * masque pas la liste à un rôle Lecture, seulement les boutons d'écriture.
 */
export interface LieuPresente {
  readonly id: string;
  readonly nom: string;
  readonly type: TypeLieu;
  readonly adresse: Adresse | null;
  readonly contactId: string | null;
  readonly notes: string;
  readonly actif: boolean;
  readonly creeLe: string;
  readonly modifieLe: string;
}

export interface CorpsLieu {
  nom: string;
  type: TypeLieu;
  adresse?: Partial<Adresse>;
  contactId?: string | null;
  notes?: string;
  actif?: boolean;
}

interface ReponseListeLieux {
  lieux: readonly LieuPresente[];
  pagination: Pagination;
}

const cleListe = (galerie: string, inclureInactifs: boolean) =>
  cleMetier(galerie, 'lieux', 'liste', inclureInactifs);
const cleFiche = (galerie: string, id: string) => cleMetier(galerie, 'lieux', 'fiche', id);

export const lieuxApi = {
  lister: (galerie: string, inclureInactifs = false, appel?: AppelApi) =>
    api.get<ReponseListeLieux>(
      `/g/${encodeURIComponent(galerie)}/lieux?parPage=200${
        inclureInactifs ? '&inclureInactifs=true' : ''
      }`,
      { galerie, ...appel },
    ),
  obtenir: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<{ lieu: LieuPresente }>(`/g/${encodeURIComponent(galerie)}/lieux/${id}`, {
      galerie,
      ...appel,
    }),
  creer: (galerie: string, corps: CorpsLieu, appel?: AppelApi) =>
    api.post<{ lieu: LieuPresente }>(`/g/${encodeURIComponent(galerie)}/lieux`, corps, {
      galerie,
      ...appel,
    }),
  modifier: (galerie: string, id: string, corps: Partial<CorpsLieu>, appel?: AppelApi) =>
    api.patch<{ lieu: LieuPresente }>(`/g/${encodeURIComponent(galerie)}/lieux/${id}`, corps, {
      galerie,
      ...appel,
    }),
  archiver: (galerie: string, id: string, appel?: AppelApi) =>
    api.post<{ lieu: LieuPresente; oeuvresRattachees: number }>(
      `/g/${encodeURIComponent(galerie)}/lieux/${id}/archiver`,
      {},
      { galerie, ...appel },
    ),
};

/** Liste des lieux, mise en cache : consommée par les filtres et les sélecteurs. */
export function useLieux(galerie: string, inclureInactifs = false) {
  return useQuery({
    queryKey: cleListe(galerie, inclureInactifs),
    queryFn: (contexte) => lieuxApi.lister(galerie, inclureInactifs, appelDeRequete(contexte)),
    select: (reponse) => reponse.lieux,
  });
}

export function useLieu(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleFiche(galerie, id ?? ''),
    queryFn: (contexte) => lieuxApi.obtenir(galerie, id ?? '', appelDeRequete(contexte)),
    select: (reponse) => reponse.lieu,
    enabled: id !== null,
  });
}

function invaliderLieux(client: ReturnType<typeof useQueryClient>, galerie: string): void {
  void client.invalidateQueries({ queryKey: cleMetier(galerie, 'lieux') });
}

export function useCreerLieu(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsLieu, appel) => lieuxApi.creer(galerie, corps, appel),
      onSuccess: () => invaliderLieux(client, galerie),
    }),
  );
}

export function useModifierLieu(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps: Partial<CorpsLieu> }, appel) =>
        lieuxApi.modifier(galerie, id, corps, appel),
      onSuccess: () => invaliderLieux(client, galerie),
    }),
  );
}

export function useArchiverLieu(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (id: string, appel) => lieuxApi.archiver(galerie, id, appel),
      onSuccess: () => invaliderLieux(client, galerie),
    }),
  );
}
